import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  X, Send, Minimize2, Maximize2, AlertCircle, Download, Flag,
  CheckCircle, Mic, MicOff, Volume2, ThumbsUp, ThumbsDown, Settings,
  Square,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { adminSupabase as supabase } from '@/integrations/supabase/adminClient';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { useToast } from '@/hooks/use-toast';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// ─── Types ──────────────────────────────────────────────────────────────────────
interface Message {
  role: 'user' | 'assistant';
  content: string;
  feedback?: 'up' | 'down' | null;
}

interface ZohanPreferences {
  voice_enabled: boolean;
  wake_word_enabled: boolean;
  auto_speak: boolean;
  preferred_language: string;
}

// ─── Zohan Avatar SVG ───────────────────────────────────────────────────────────
function ZohanAvatar({ size = 24, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className={className}>
      <circle cx="16" cy="16" r="15" fill="hsl(var(--primary))" opacity="0.15" />
      <circle cx="16" cy="16" r="12" fill="hsl(var(--primary))" opacity="0.25" />
      <text x="16" y="21" textAnchor="middle" fontSize="14" fontWeight="700" fill="hsl(var(--primary))">Z</text>
    </svg>
  );
}

// ─── Greeting ───────────────────────────────────────────────────────────────────
function getGreeting(name?: string | null): string {
  const hour = new Date().getHours();
  const firstName = name?.split(' ')[0];
  const greetName = firstName ? `, ${firstName}` : '';

  if (hour >= 6 && hour < 12) return `Good morning${greetName}! I'm Zohan, your operations assistant. How can I help today?`;
  if (hour >= 12 && hour < 18) return `Good afternoon${greetName}! What can I help you with?`;
  if (hour >= 18 && hour < 22) return `Good evening${greetName}! Working late? I'm here to help.`;
  return `Burning the midnight oil${greetName}? Let me help you get through it faster.`;
}

// ─── Suggested Questions (page-aware) ───────────────────────────────────────────
const SUGGESTED_QUESTIONS: Record<string, string[]> = {
  '/admin/applications/individual': ['How do I approve an application?', 'What does EDD Required mean?', 'How do I reject with a reason?'],
  '/admin/applications/corporate': ['How do I verify company documents?', 'What is a KYB check?', 'How do I add a UBO?'],
  '/admin/compliance/command-center': ['What should I do first each morning?', 'How do I clear a screening alert?', 'What does the SLA clock mean?'],
  '/admin/compliance/screening': ['How do I disposition a PEP match?', 'What is a false positive?', 'When should I escalate to EDD?'],
  '/admin/compliance/alerts': ['Explain this alert', 'Help me disposition', 'Show similar past alerts'],
  '/admin/compliance/cases': ['Help investigate', 'Draft case summary', 'What evidence is needed?'],
  '/admin/compliance/interdictions': ['Why is this held?', 'How to release a payment?', 'How to cancel a held payment?'],
  '/admin/approvals': ['How does 4-eyes approval work?', 'Who can approve a fund freeze?', 'What happens if I reject an approval?'],
  '/admin/treasury': ['Current FX exposure?', 'EOD checklist', 'How do I update spread rules?'],
  '/admin/ledger/safeguarding': ['Explain discrepancy', 'Daily recon steps', 'Who do I escalate to?'],
  '/admin/ops/incidents': ['How do I log a P1 incident?', 'What is PSD2 reportable?', 'How do I close an incident?'],
  '/admin/payments/outbound': ['Why did this fail?', 'SEPA cut-off time?', 'How to retry a payment?'],
  '/admin/cards': ['How do I block a card?', 'How do I reset a PIN?', 'How do I set card limits?'],
  '/admin/clients': ['How do I freeze a client account?', 'What does the risk score mean?', 'How do I update client details?'],
  '/admin/support': ['Suggest a reply', 'Escalation path', 'Check SLA'],
  '/admin/compliance/sar': ['What is tipping-off?', 'How do I file a SAR?', 'Who approves SAR submissions?'],
};

function getSuggestions(path: string): string[] {
  const exact = SUGGESTED_QUESTIONS[path];
  if (exact) return exact;
  const match = Object.keys(SUGGESTED_QUESTIONS)
    .filter(k => path.startsWith(k))
    .sort((a, b) => b.length - a.length)[0];
  return match ? SUGGESTED_QUESTIONS[match] : ['How does this page work?', 'What are the key actions here?', 'Walk me through the workflow'];
}

// ─── Chat URL ───────────────────────────────────────────────────────────────────
const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/zohan-assistant`;

// ─── Speech helpers ─────────────────────────────────────────────────────────────
const SpeechRecognitionAPI = typeof window !== 'undefined'
  ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
  : null;

function speak(text: string, onEnd?: () => void) {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text.replace(/[#*_`]/g, ''));
  utterance.rate = 1.0;
  utterance.pitch = 1.0;
  utterance.lang = 'en-US';
  const voices = window.speechSynthesis.getVoices();
  const preferred = voices.find(v => v.name.includes('Google') || v.name.includes('Natural') || v.name.includes('Samantha'));
  if (preferred) utterance.voice = preferred;
  if (onEnd) utterance.onend = onEnd;
  window.speechSynthesis.speak(utterance);
}

function playChime() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = 880;
    osc.type = 'sine';
    gain.gain.setValueAtTime(0.15, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch { /* silent fail */ }
}

// ─── COMPONENT ──────────────────────────────────────────────────────────────────
export function ZohanAssistant() {
  const location = useLocation();
  const { adminUser } = useAdminAuth();
  const { toast } = useToast();

  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasNewMsg, setHasNewMsg] = useState(false);
  const [reportMode, setReportMode] = useState(false);
  const [reportIssue, setReportIssue] = useState('');
  const [reportSeverity, setReportSeverity] = useState<'low' | 'medium' | 'high'>('medium');
  const [reportSent, setReportSent] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Voice state
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [prefs, setPrefs] = useState<ZohanPreferences>({
    voice_enabled: false,
    wake_word_enabled: false,
    auto_speak: false,
    preferred_language: 'en',
  });

  const bottomRef = useRef<HTMLDivElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const recognitionRef = useRef<any>(null);
  const wakeRef = useRef<any>(null);

  const greeting = useMemo(() => getGreeting(adminUser?.fullName || adminUser?.email), [adminUser]);
  const suggestions = getSuggestions(location.pathname);

  // ── Load preferences on mount ──
  useEffect(() => {
    if (!adminUser?.userId) return;
    fetch(CHAT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}` },
      body: JSON.stringify({ action: 'load_preferences', admin_user_id: adminUser.userId }),
    })
      .then(r => r.json())
      .then(d => { if (d.preferences) setPrefs(d.preferences); })
      .catch(() => {});
  }, [adminUser?.userId]);

  // ── Reset on page change ──
  useEffect(() => {
    setMessages([]);
    setError(null);
    setReportMode(false);
    setReportSent(false);
    setConversationId(null);
  }, [location.pathname]);

  // ── Auto-scroll ──
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // ── Wake word listener ──
  useEffect(() => {
    if (!prefs.wake_word_enabled || !SpeechRecognitionAPI) return;
    const wake = new SpeechRecognitionAPI();
    wake.continuous = true;
    wake.interimResults = true;
    wake.lang = 'en-US';
    wake.onresult = (event: any) => {
      const transcript = Array.from(event.results as SpeechRecognitionResultList)
        .map((r: any) => r[0].transcript.toLowerCase())
        .join(' ');
      if (transcript.includes('hey zohan') || transcript.includes('zohan')) {
        wake.stop();
        setOpen(true);
        setMinimized(false);
        playChime();
        setTimeout(() => startListening(), 500);
      }
    };
    wake.onerror = () => {};
    wake.onend = () => {
      if (prefs.wake_word_enabled && !open) {
        try { wake.start(); } catch { /* ignore */ }
      }
    };
    try { wake.start(); } catch { /* ignore */ }
    wakeRef.current = wake;
    return () => { try { wake.stop(); } catch { /* ignore */ } };
  }, [prefs.wake_word_enabled, open]);

  // ── Speech recognition ──
  const startListening = useCallback(() => {
    if (!SpeechRecognitionAPI) {
      toast({ title: "Speech recognition not supported", description: "Please use Chrome or Edge.", variant: "destructive" });
      return;
    }
    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onresult = (event: any) => {
      const transcript = Array.from(event.results as SpeechRecognitionResultList)
        .map((r: any) => r[0].transcript)
        .join('');
      setInput(transcript);
      // Auto-submit on final result
      if (event.results[event.results.length - 1].isFinal) {
        setTimeout(() => {
          setIsListening(false);
          if (transcript.trim()) send(transcript.trim());
        }, 300);
      }
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  }, []);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
    setIsListening(false);
  }, []);

  const stopSpeaking = useCallback(() => {
    window.speechSynthesis?.cancel();
    setIsSpeaking(false);
  }, []);

  const speakText = useCallback((text: string) => {
    setIsSpeaking(true);
    speak(text, () => setIsSpeaking(false));
  }, []);

  // ── Send message ──
  const send = useCallback(async (text: string) => {
    if (!text.trim() || isStreaming) return;
    setError(null);
    const userMsg: Message = { role: 'user', content: text.trim() };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInput('');
    setIsStreaming(true);
    abortRef.current = new AbortController();

    try {
      const resp = await fetch(CHAT_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
          currentPage: location.pathname,
          adminName: adminUser?.fullName || adminUser?.email,
          adminRole: adminUser?.roles?.[0] || '',
        }),
        signal: abortRef.current.signal,
      });

      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.error || `Error ${resp.status}`);
      }
      if (!resp.body) throw new Error('No response body');

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantContent = '';
      setMessages(prev => [...prev, { role: 'assistant', content: '' }]);

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx: number;
        while ((idx = buffer.indexOf('\n')) !== -1) {
          let line = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 1);
          if (line.endsWith('\r')) line = line.slice(0, -1);
          if (line.startsWith(':') || !line.trim()) continue;
          if (!line.startsWith('data: ')) continue;
          const jsonStr = line.slice(6).trim();
          if (jsonStr === '[DONE]') break;
          try {
            const parsed = JSON.parse(jsonStr);
            const chunk = parsed.choices?.[0]?.delta?.content;
            if (chunk) {
              assistantContent += chunk;
              setMessages(prev =>
                prev.map((m, i) =>
                  i === prev.length - 1 && m.role === 'assistant' ? { ...m, content: assistantContent } : m
                )
              );
            }
          } catch {
            buffer = line + '\n' + buffer;
            break;
          }
        }
      }

      // Auto-speak if enabled
      if (prefs.auto_speak && assistantContent) {
        speakText(assistantContent);
      }

      // Save conversation
      if (adminUser?.userId) {
        fetch(CHAT_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}` },
          body: JSON.stringify({
            action: 'save_conversation',
            admin_user_id: adminUser.userId,
            admin_email: adminUser.email,
            admin_name: adminUser.fullName,
            current_page: location.pathname,
            messages: [...newMessages, { role: 'assistant', content: assistantContent }],
            voice_used: isListening,
          }),
        }).then(r => r.json()).then(d => { if (d.conversation_id) setConversationId(d.conversation_id); }).catch(() => {});
      }

      if (!open) setHasNewMsg(true);
    } catch (e: unknown) {
      if ((e as Error)?.name === 'AbortError') return;
      setError((e as Error)?.message || 'Something went wrong.');
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant' && !last.content) return prev.slice(0, -1);
        return prev;
      });
    } finally {
      setIsStreaming(false);
    }
  }, [messages, isStreaming, location.pathname, open, adminUser, prefs.auto_speak, isListening, speakText]);

  // ── Feedback ──
  const sendFeedback = useCallback((msgIdx: number, helpful: boolean) => {
    setMessages(prev => prev.map((m, i) => i === msgIdx ? { ...m, feedback: helpful ? 'up' : 'down' } : m));
    if (conversationId && adminUser?.userId) {
      fetch(CHAT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}` },
        body: JSON.stringify({
          action: 'save_feedback',
          conversation_id: conversationId,
          was_helpful: helpful,
          admin_user_id: adminUser.userId,
        }),
      }).catch(() => {});
    }
  }, [conversationId, adminUser]);

  // ── Save preferences ──
  const updatePref = useCallback((key: keyof ZohanPreferences, value: boolean | string) => {
    const newPrefs = { ...prefs, [key]: value };
    setPrefs(newPrefs);
    if (adminUser?.userId) {
      fetch(CHAT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}` },
        body: JSON.stringify({ action: 'save_preferences', admin_user_id: adminUser.userId, preferences: newPrefs }),
      }).catch(() => {});
    }
  }, [prefs, adminUser]);

  const handleOpen = () => { setOpen(true); setHasNewMsg(false); setMinimized(false); };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(input); }
  };

  const exportConversation = () => {
    const page = location.pathname;
    const date = new Date().toLocaleString();
    let md = `# Zohan Session\n\n**Page:** ${page}\n**Date:** ${date}\n\n---\n\n`;
    messages.forEach(m => {
      md += `**${m.role === 'user' ? '👤 You' : '✨ Zohan'}:**\n\n${m.content}\n\n---\n\n`;
    });
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `zohan-session-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const submitReport = async () => {
    if (!reportIssue.trim()) return;
    setReportLoading(true);
    try {
      await supabase.from('ai_dev_reports' as any).insert({
        page_path: location.pathname,
        reporter_user_id: adminUser?.userId || null,
        reporter_email: adminUser?.email || null,
        issue_summary: reportIssue.trim(),
        severity: reportSeverity,
        conversation_context: messages.length > 0
          ? messages.map(m => ({ role: m.role, content: m.content.slice(0, 500) }))
          : null,
        status: 'open',
      });
      setReportSent(true);
      setReportMode(false);
      setReportIssue('');
    } catch {
      setError('Could not submit report.');
    } finally {
      setReportLoading(false);
    }
  };

  // ── Render ──
  return (
    <>
      {/* Floating trigger */}
      {!open && (
        <div className="fixed bottom-[88px] right-6 z-40">
          <button
            onClick={handleOpen}
            title="Hey Zohan — AI Assistant"
            className={cn(
              "h-12 w-12 rounded-full shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center border border-border relative",
              "bg-gradient-to-br from-primary/20 to-primary/5 text-primary"
            )}
          >
            <ZohanAvatar size={28} />
            {hasNewMsg && (
              <span className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-destructive border-2 border-background" />
            )}
            {isListening && (
              <span className="absolute inset-0 rounded-full border-2 border-primary animate-ping opacity-30" />
            )}
          </button>
        </div>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed bottom-24 right-6 z-40 w-[min(420px,calc(100vw-2rem))] flex flex-col rounded-2xl border border-border bg-card shadow-2xl overflow-hidden animate-in slide-in-from-bottom-4 duration-200"
          style={{ height: 'min(560px, calc(100vh - 120px))' }}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-primary/90 to-primary px-4 py-3 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center">
                <span className="text-white font-bold text-sm">Z</span>
              </div>
              <div>
                <p className="font-semibold text-sm leading-tight text-white">Zohan</p>
                <p className="text-[10px] opacity-70 leading-tight text-white/80">Operations Intelligence</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && !reportMode && !settingsOpen && (
                <>
                  <button onClick={exportConversation} title="Export" className="text-white/60 hover:text-white p-1 rounded transition-opacity">
                    <Download className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={() => { setReportMode(true); setReportSent(false); }} title="Report issue" className="text-white/60 hover:text-white p-1 rounded transition-opacity">
                    <Flag className="h-3.5 w-3.5" />
                  </button>
                </>
              )}
              <button onClick={() => setSettingsOpen(s => !s)} title="Settings" className="text-white/60 hover:text-white p-1 rounded transition-opacity">
                <Settings className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => setMinimized(m => !m)} className="text-white/60 hover:text-white p-1 rounded transition-opacity">
                {minimized ? <Maximize2 className="h-3.5 w-3.5" /> : <Minimize2 className="h-3.5 w-3.5" />}
              </button>
              <button onClick={() => setOpen(false)} title="Close" className="text-white/80 hover:text-white p-1.5 rounded-md transition-opacity hover:bg-white/10 ml-0.5">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {!minimized && (
            <div className="flex flex-col flex-1 min-h-0">
              {/* Settings panel */}
              {settingsOpen ? (
                <div className="p-4 space-y-4">
                  <p className="text-sm font-semibold">Zohan Settings</p>
                  <div className="space-y-3">
                    <label className="flex items-center justify-between">
                      <span className="text-xs">Voice input (microphone)</span>
                      <Switch checked={prefs.voice_enabled} onCheckedChange={v => updatePref('voice_enabled', v)} />
                    </label>
                    <label className="flex items-center justify-between">
                      <span className="text-xs">Wake word ("Hey Zohan")</span>
                      <Switch checked={prefs.wake_word_enabled} onCheckedChange={v => updatePref('wake_word_enabled', v)} />
                    </label>
                    <label className="flex items-center justify-between">
                      <span className="text-xs">Auto-speak responses</span>
                      <Switch checked={prefs.auto_speak} onCheckedChange={v => updatePref('auto_speak', v)} />
                    </label>
                  </div>
                  <p className="text-[10px] text-muted-foreground">Wake word requires microphone permission and works best in Chrome.</p>
                  <Button size="sm" variant="outline" className="w-full text-xs" onClick={() => setSettingsOpen(false)}>Done</Button>
                </div>
              ) : reportMode ? (
                <div className="p-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold mb-1">Report an Issue</p>
                    <p className="text-xs text-muted-foreground">Describe the problem — it will be sent to the dev team with conversation context.</p>
                  </div>
                  <Textarea
                    value={reportIssue}
                    onChange={e => setReportIssue(e.target.value)}
                    placeholder="Describe the issue in detail..."
                    className="text-xs min-h-[80px] resize-none"
                    disabled={reportLoading}
                  />
                  <div className="space-y-1">
                    <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Severity</p>
                    <div className="flex gap-2">
                      {(['low', 'medium', 'high'] as const).map(s => (
                        <button
                          key={s}
                          onClick={() => setReportSeverity(s)}
                          className={cn(
                            'flex-1 text-xs px-2 py-1 rounded-lg border transition-colors capitalize',
                            reportSeverity === s
                              ? s === 'high' ? 'bg-red-500/20 border-red-500/40 text-red-600 dark:text-red-400'
                                : s === 'medium' ? 'bg-amber-500/20 border-amber-500/40 text-amber-600 dark:text-amber-400'
                                : 'bg-emerald-500/20 border-emerald-500/40 text-emerald-600 dark:text-emerald-400'
                              : 'border-border text-muted-foreground hover:bg-muted/50'
                          )}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => setReportMode(false)} disabled={reportLoading}>Cancel</Button>
                    <Button size="sm" className="flex-1 text-xs" onClick={submitReport} disabled={!reportIssue.trim() || reportLoading}>
                      {reportLoading ? 'Sending…' : 'Send Report'}
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {reportSent && (
                    <div className="mx-3 mt-3 flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 text-xs text-emerald-600 dark:text-emerald-400">
                      <CheckCircle className="h-3.5 w-3.5 shrink-0" />
                      Report submitted to the dev team.
                    </div>
                  )}

                  {/* Messages */}
                  <ScrollArea className="flex-1 min-h-0">
                    <div className="p-3 space-y-3">
                      {messages.length === 0 && (
                        <div className="space-y-3">
                          <div className="flex items-start gap-2">
                            <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                              <ZohanAvatar size={20} />
                            </div>
                            <div className="bg-muted rounded-xl rounded-tl-sm px-3 py-2 text-xs text-foreground leading-relaxed max-w-[280px]">
                              {greeting}
                            </div>
                          </div>
                          <div className="pl-9 space-y-1.5">
                            <p className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">Quick actions</p>
                            {suggestions.map(q => (
                              <button
                                key={q}
                                onClick={() => send(q)}
                                disabled={isStreaming}
                                className="block w-full text-left text-xs text-primary hover:text-primary/80 hover:bg-primary/5 px-2 py-1.5 rounded-lg border border-primary/20 transition-colors disabled:opacity-50"
                              >
                                {q}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {messages.map((msg, i) => (
                        <div key={i}>
                          <div className={cn('flex items-start gap-2', msg.role === 'user' && 'flex-row-reverse')}>
                            {msg.role === 'assistant' && (
                              <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                                <ZohanAvatar size={18} />
                              </div>
                            )}
                            <div
                              className={cn(
                                'px-3 py-2 rounded-xl text-xs leading-relaxed min-w-0 overflow-hidden',
                                msg.role === 'user'
                                  ? 'bg-primary text-primary-foreground rounded-tr-sm max-w-[320px]'
                                  : 'bg-muted text-foreground rounded-tl-sm flex-1 max-w-[calc(100%-2rem)]'
                              )}
                            >
                              {msg.content ? (
                                msg.role === 'assistant' ? (
                                  <div className="prose prose-xs dark:prose-invert max-w-none text-xs leading-relaxed break-words
                                    [&>*]:my-1.5 [&>*:first-child]:mt-0 [&>*:last-child]:mb-0
                                    [&_p]:my-1 [&_strong]:text-foreground [&_strong]:font-semibold
                                    [&_ul]:my-1.5 [&_ul]:pl-4 [&_ul]:list-disc [&_ol]:my-1.5 [&_ol]:pl-4
                                    [&_li]:my-0.5 [&_li]:marker:text-muted-foreground
                                    [&_code]:bg-background/60 [&_code]:px-1 [&_code]:py-px [&_code]:rounded [&_code]:text-[11px] [&_code]:font-mono [&_code]:text-primary [&_code]:break-all
                                    [&_pre]:bg-background/60 [&_pre]:p-2 [&_pre]:rounded-md [&_pre]:overflow-x-auto [&_pre]:max-w-full
                                    [&_table]:block [&_table]:w-full [&_table]:max-w-full [&_table]:overflow-x-auto [&_table]:my-2 [&_table]:border-collapse [&_table]:text-[11px] [&_table]:whitespace-nowrap
                                    [&_th]:bg-background/40 [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_th]:font-semibold [&_th]:border [&_th]:border-border
                                    [&_td]:px-2 [&_td]:py-1 [&_td]:align-top [&_td]:border [&_td]:border-border
                                    [&_h1]:text-sm [&_h2]:text-sm [&_h3]:text-xs [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold
                                    [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a]:break-all
                                    [&_blockquote]:border-l-2 [&_blockquote]:border-primary/40 [&_blockquote]:pl-2 [&_blockquote]:text-muted-foreground">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                                  </div>
                                ) : (
                                  <span className="whitespace-pre-wrap">{msg.content}</span>
                                )
                              ) : (
                                <span className="flex gap-1 items-center">
                                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:0ms]" />
                                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:150ms]" />
                                  <span className="h-1.5 w-1.5 rounded-full bg-muted-foreground/50 animate-bounce [animation-delay:300ms]" />
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Feedback row */}
                          {msg.role === 'assistant' && msg.content && !isStreaming && (
                            <div className="pl-8 mt-1 flex items-center gap-1">
                              <button
                                onClick={() => sendFeedback(i, true)}
                                className={cn('p-0.5 rounded transition-colors', msg.feedback === 'up' ? 'text-emerald-500' : 'text-muted-foreground/40 hover:text-muted-foreground')}
                                title="Helpful"
                              >
                                <ThumbsUp className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => sendFeedback(i, false)}
                                className={cn('p-0.5 rounded transition-colors', msg.feedback === 'down' ? 'text-red-500' : 'text-muted-foreground/40 hover:text-muted-foreground')}
                                title="Not helpful"
                              >
                                <ThumbsDown className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => speakText(msg.content)}
                                className="p-0.5 rounded text-muted-foreground/40 hover:text-muted-foreground transition-colors"
                                title="Read aloud"
                              >
                                <Volume2 className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}

                      {error && (
                        <div className="flex items-start gap-2 text-destructive">
                          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                          <p className="text-xs">{error}</p>
                        </div>
                      )}

                      <div ref={bottomRef} />
                    </div>
                  </ScrollArea>

                  {/* Speaking indicator */}
                  {isSpeaking && (
                    <div className="px-3 py-1.5 bg-primary/5 border-t flex items-center justify-between">
                      <span className="text-[10px] text-primary flex items-center gap-1.5">
                        <Volume2 className="h-3 w-3 animate-pulse" /> Zohan is speaking...
                      </span>
                      <button onClick={stopSpeaking} className="text-[10px] text-muted-foreground hover:text-foreground">
                        <Square className="h-3 w-3" />
                      </button>
                    </div>
                  )}

                  {/* Listening indicator */}
                  {isListening && (
                    <div className="px-3 py-1.5 bg-emerald-500/5 border-t flex items-center justify-between">
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                        <Mic className="h-3 w-3 animate-pulse" /> Listening...
                      </span>
                      <button onClick={stopListening} className="text-[10px] text-muted-foreground hover:text-foreground">Stop</button>
                    </div>
                  )}

                  {/* Input */}
                  <div className="border-t px-3 py-2 shrink-0 bg-card">
                    <div className="flex gap-2 items-end">
                      {prefs.voice_enabled && (
                        <Button
                          size="icon"
                          variant={isListening ? 'default' : 'outline'}
                          className={cn('h-9 w-9 shrink-0', isListening && 'bg-emerald-500 hover:bg-emerald-600')}
                          onClick={isListening ? stopListening : startListening}
                          disabled={isStreaming}
                          title={isListening ? 'Stop listening' : 'Start voice input'}
                        >
                          {isListening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
                        </Button>
                      )}
                      <Textarea
                        value={input}
                        onChange={e => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask Zohan anything…"
                        className="flex-1 min-h-[36px] max-h-[100px] text-xs resize-none"
                        disabled={isStreaming}
                        rows={1}
                      />
                      <Button
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        onClick={() => send(input)}
                        disabled={!input.trim() || isStreaming}
                      >
                        <Send className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                    <p className="text-[9px] text-muted-foreground/50 mt-1.5 text-center">
                      Shift+Enter for new line · <button className="underline hover:opacity-80" onClick={() => setReportMode(true)}>Report an issue</button>
                    </p>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
